// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { useSelector } from "react-redux";
import React, { useCallback, useMemo } from "react";
import * as R from "ramda";

import Item from "./Item";
import FetchedItem from "./FetchedItem";
import { List as StyledList } from "./styles";
import VirtualList from "src/components/VirtualList";
import { RemoveOption } from "../Search/styles";
import Icon from "src/icons";
import {
  getFilteredChatroomExcluding,
  filterCurrentVersions,
} from "src/reducers";
import { useSarah, Text, Flex } from "@unifize/sarah";
import { filterCurrentVersionsFromServer } from "./utils";

import type { RoomId } from "src/types";

type Props = {
  roomId: RoomId,
  showRemove: boolean,
  groupVersions: boolean,
  selectedValue: Array<number>,
  handleSelect: Function,
  handleRemove?: Function,
  selectNewRoomVersions?: Function,
  hideSelectedVersions?: boolean,
  dependentInclude?: ?Array<number>,
  dependentExclude?: ?Array<number>,
  alwaysShowRevisionIcon: boolean,
  chatRooms?: Object,
  isDataProcess?: boolean,
  hasDynamicFilter?: boolean,
};

const List = ({
  roomId,
  showRemove,
  selectedValue,
  hideSelectedVersions = false,
  groupVersions = false,
  selectNewRoomVersions,
  handleSelect,
  handleRemove,
  dependentInclude,
  dependentExclude,
  alwaysShowRevisionIcon,
  chatRooms = {},
  isDataProcess = false,
  hasDynamicFilter,
}: Props) => {
  const { colors } = useSarah();
  const rooms = R.uniq([parseInt(roomId, 10), ...selectedValue]);
  const results = useSelector((state) =>
    getFilteredChatroomExcluding(state.app, rooms),
  );
  const filteredByVersions = useSelector((state) =>
    filterCurrentVersions(
      state.app,
      results,
      hideSelectedVersions ? selectedValue : null,
    ),
  );

  // Function to filter by version when chatRooms
  // are not present in state or isDataProcess is true.
  const filteredByVersionsFromServer =
    isDataProcess && !R.isEmpty(chatRooms)
      ? filterCurrentVersionsFromServer(
          chatRooms,
          results,
          hideSelectedVersions ? selectedValue : null,
        )
      : [];

  const result = groupVersions
    ? isDataProcess
      ? filteredByVersionsFromServer
      : filteredByVersions
    : results;

  const filteredResults: Array<number> = useMemo(() => {
    if (dependentInclude?.length) {
      return R.uniq(dependentInclude || []);
    } else if (dependentExclude?.length) {
      return R.uniq(R.without(dependentExclude || [], result));
    }

    return R.uniq(result);
  }, [result, dependentInclude, dependentExclude]);
  // $FlowFixMe - Type coercion to string

  const renderRow = useCallback(
    (index: number) => {
      const room = filteredResults[index];
      if (room) {
        return isDataProcess ? (
          <FetchedItem
            key={room}
            id={room}
            chatRooms={chatRooms}
            handleSelect={handleSelect}
            selectNewRoomVersions={selectNewRoomVersions}
            alwaysShowRevisionIcon={alwaysShowRevisionIcon}
          />
        ) : (
          <Item
            key={room}
            id={room}
            handleSelect={handleSelect}
            selectNewRoomVersions={selectNewRoomVersions}
            alwaysShowRevisionIcon={alwaysShowRevisionIcon}
          />
        );
      }
      return <></>;
    },
    [result, handleSelect, selectNewRoomVersions],
  );

  return (
    <StyledList data-cy="conversation-dropdown">
      {(isDataProcess || hasDynamicFilter) && results.length === 12 && (
        <Flex
          justify="center"
          items="center"
          padding={3}
          background={colors.background.primary.subtle}
        >
          <Text variant="caption" weight="bold" color={colors.text.secondary}>
            {/* The fetch API has a limit of 12 results, so we show the count to 12 always. */}
            {i18n.t(k.SHOWING_RESULTS_SEARCH_TO_FIND)}
          </Text>
        </Flex>
      )}
      <ul tabIndex="-1">
        {showRemove && handleRemove && (
          <RemoveOption data-cy="clear-conversation" onClick={handleRemove}>
            {i18n.t(k.REMOVE)}
            <Icon type="checklist-remove" />
          </RemoveOption>
        )}

        <VirtualList
          initialAmount={20}
          progressiveAmount={20}
          role="list"
          renderItem={renderRow}
          renderLoader={() => <div />}
          rowCount={filteredResults.length}
        />
      </ul>
    </StyledList>
  );
};

export default List;
